import { useTranslation } from 'react-i18next';
import { notification } from 'antd';
import { adminMediaType } from '@propTypes';
import routes from '@root/routes';
import { VirtualTourSpotlights } from '@root/types';
import api from '@shared/api';
import type { DefaultPhotoUploadType } from '@components/shared/Admin/MediaUploader';
import MediaPhotosSection from '@components/shared/Admin/EditPhotos/MediaPhotosSection';
import VideoTable from '@components/shared/Admin/VideoTable';
import ThreeDContent from '@components/shared/Admin/ThreeDContent/ThreeDContent';
import GenerateMediaDirectoryButton from '@root/components/shared/GenerateMediaDirectoryButton';
import s from './BuildingMediaTable.module.less';

type OwnProps = {
  fetchAdminBuilding: $TSFixMeFunction;
  photos?: adminMediaType[];
  video?: adminMediaType;
  googleDriveMediaDirectoryId?: string;
  googleDriveMediaDirectoryIdTwo?: string;
  buildingId: string;
  tourEmbedUrl: string | null;
  firstPhotoOrientation: 'landscape' | 'portrait';
  virtualTourSpotlight: VirtualTourSpotlights;
  formattedAddress: string;
};

// @ts-expect-error ts-migrate(2456) FIXME: Type alias 'Props' circularly references itself.
type Props = OwnProps & typeof BuildingMediaTable.defaultProps;

const BuildingMediaTable = ({
  photos = [],
  video = null,
  googleDriveMediaDirectoryId = null,
  googleDriveMediaDirectoryIdTwo = null,
  fetchAdminBuilding,
  buildingId,
  firstPhotoOrientation,
  tourEmbedUrl,
  virtualTourSpotlight,
  formattedAddress,
}: Props) => {
  const { t } = useTranslation('admin');

  const handleUploadPhotos = (uploadedFiles: DefaultPhotoUploadType[]) =>
    api.post(routes.api.admin.buildingPhotosCreate(buildingId), {
      photos: uploadedFiles,
    });

  const handleUpdatePhotoPosition = async (photoIds: string[]) => {
    const response = await api.put(routes.api.admin.buildingPhotosOrder(buildingId), {
      photo_ids: photoIds,
    });

    if (response.ok) {
      notification.success({ message: t('common.photos.orderSaveSuccess') });
      fetchAdminBuilding();
    } else {
      notification.error({ message: t('common.photos.orderSaveFailure') });
    }
  };

  const handleDeletePhoto = async (buildingPhotoId: string) => {
    const response = await api.delete(routes.api.admin.buildingPhoto(buildingId, buildingPhotoId));
    const json = await response.json();

    if (response.ok) {
      await fetchAdminBuilding();
      notification.success({ message: json.message });
    } else {
      notification.error({ message: json.errors[0].message });
    }
  };

  const handleFirstPhotoOrientationChange = async (newOrientation: string) => {
    const response = await api.put(routes.api.admin.building(buildingId), {
      firstPhotoOrientation: newOrientation,
    });
    const json = await response.json();

    if (response.ok) {
      await fetchAdminBuilding();
      notification.success({
        message:
          newOrientation === 'landscape' ? t('media.landscapeSuccess') : t('media.portraitSuccess'),
      });
    } else {
      notification.error({ message: json.errors[0].message });
    }
  };

  const videoUploadRequest = uploadedFile =>
    api.post(routes.api.admin.buildingVideoCreate(buildingId), {
      cloudinary_id: uploadedFile.cloudinaryId,
      filename: uploadedFile.filename,
      processed_at: uploadedFile.processedAt,
    });

  const updateVirtualTourSpotlight = async (newValue: Exclude<VirtualTourSpotlights, 'unset'>) => {
    const resp = await api.put(routes.api.admin.building(buildingId), {
      virtualTourSpotlight: newValue,
    });

    if (resp.ok) {
      fetchAdminBuilding();
      return true;
    }
    return false;
  };

  const deleteBuildingVideo = async () => {
    // eslint-disable-next-line no-alert
    if (window.confirm(t('media.deleteConfirmation'))) {
      const resp = await api.delete(routes.api.admin.buildingVideo(buildingId, video?.id));
      const json = await resp.json();
      if (resp.ok) {
        fetchAdminBuilding();
        notification.success({ message: json.message });
      } else {
        notification.error({ message: json.errors[0].message });
      }
    }
  };

  const updateThumbnailPosterTimestamp = async (posterStartOffset: number | null) => {
    const resp = await api.put(routes.api.admin.buildingVideo(buildingId, video?.id), {
      posterStartOffset,
    });
    return resp;
  };

  const handleUpdateTourEmbedUrl = async (value: string) => {
    const resp = await api.put(routes.api.admin.building(buildingId), {
      tour_embed_url: value,
    });
    const json = await resp.json();

    if (resp.ok) {
      fetchAdminBuilding();
      return true;
    }
    notification.error({ message: json.messages });
    return false;
  };

  return (
    <>
      <MediaPhotosSection
        title={t('media.buildingPhotosTitle')}
        photos={photos}
        googleDriveMediaDirectoryId={googleDriveMediaDirectoryId}
        googleDriveMediaDirectoryIdTwo={googleDriveMediaDirectoryIdTwo}
        fetchRecord={fetchAdminBuilding}
        onPhotoUpload={handleUploadPhotos}
        onUpdatePhotoPosition={handleUpdatePhotoPosition}
        onDeletePhoto={handleDeletePhoto}
        onFirstPhotoOrientationChange={handleFirstPhotoOrientationChange}
        firstPhotoOrientation={firstPhotoOrientation}
      />
      <div className="u-p-top-5x">
        <VideoTable
          data-testid="video-table-container"
          key={video?.id}
          video={video}
          onSuccess={fetchAdminBuilding}
          spotlightChecked={!!video && !!tourEmbedUrl}
          videoTableTitle={t('media.buildingVideoTitle')}
          videoUploadRequest={videoUploadRequest}
          onVideoDelete={deleteBuildingVideo}
          onPosterUpdate={updateThumbnailPosterTimestamp}
          onUpdateVirtualTourSpotlight={updateVirtualTourSpotlight}
          virtualTourSpotlight={virtualTourSpotlight}
        />
      </div>
      <div className="u-p-top-5x">
        <ThreeDContent
          tourEmbedUrl={tourEmbedUrl}
          onVirtualTourSpotlightUpdate={updateVirtualTourSpotlight}
          onTourEmbedUrlUpdate={handleUpdateTourEmbedUrl}
          hasVideo={!!video}
          virtualTourSpotlight={virtualTourSpotlight}
          tableTitle={t('media.building3dContentTitle')}
        />
      </div>
      {/* eslint-disable-next-line no-nested-ternary */}
      {googleDriveMediaDirectoryIdTwo ? null : (
        <div className="u-p-top-5x">
          <div className={s.generateMediaSection}>
            <GenerateMediaDirectoryButton
              entityId={buildingId}
              afterCreate={() => fetchAdminBuilding()}
              disabled={!formattedAddress}
              route={routes.api.admin.buildingMediaDirectory}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default BuildingMediaTable;
